import { FormControl, FormGroup, FormLabel, Grid, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import LabelledCheckbox from "../../../components/labelledCheckbox/LabelledCheckbox";
import NumberField from "../../../components/numberField/NumberField";
import dayjs, { ISO_DATE_FORMAT } from "../../../dayjs";
import type { AdminProductOptionResponse } from "../productsApi";
import { PriceMode } from "./PriceModeSelector";
import DateRangeGroup from "./dateRangeGroup/DateRangeGroup";
import { useOptionPricesBatch } from "./useOptionPricesBatch";

const priceLists = [
  {
    id: PriceMode.PURCHASE_PRICE,
    label: "Ceny zakupu",
  },
  {
    id: PriceMode.SELLING_PRICE,
    label: "Ceny sprzedaży",
  },
  {
    id: PriceMode.FIT_PRICE,
    label: "Ceny FIT",
  },
];
const possibleChanges = (valueMode: "MULTIPLIER" | "AMOUNT") => [
  {
    id: PriceMode.PURCHASE_PRICE,
    label: valueMode === "MULTIPLIER" ? "Mnożnik ceny zakupu" : "Cena zakupu powiększona o",
  },
  {
    id: PriceMode.SELLING_PRICE,
    label: valueMode === "MULTIPLIER" ? "Mnożnik ceny sprzedaży" : "Cena sprzedaży powiększona o",
  },
  {
    id: PriceMode.FIT_PRICE,
    label: valueMode === "MULTIPLIER" ? "Mnożnik ceny FIT" : "Cena FIT powiększona o",
  },
];
export const daysOfWeek = ["pn", "wt", "sr", "cz", "pt", "sb", "nd"];

interface OptionProps {
  readonly name: string;
  readonly selected: boolean | undefined;
  readonly onSelectionChange: (selected: boolean) => void;
}

const Option: FC<OptionProps> = ({ name, selected, onSelectionChange }) => (
  <Grid
    size={12}
    data-testid="product-option"
    sx={{
      alignItems: "center",
    }}
  >
    <LabelledCheckbox label={name} checked={selected ?? false} onChange={onSelectionChange} />
  </Grid>
);

interface PriceListSelectProps {
  label: string;
  disabled?: boolean;
  values: { id: PriceMode; label: string; selected: boolean }[];
  onSelectionChange: (id: PriceMode, selected: boolean) => void;
}

const PriceListSelect: FC<PriceListSelectProps> = ({ label, disabled, values, onSelectionChange }) => (
  <FormControl sx={{ m: 3 }} component="fieldset" variant="standard" disabled={disabled}>
    <FormLabel component="legend">{label}</FormLabel>
    <FormGroup>
      {values.map(({ id, label, selected }) => (
        <LabelledCheckbox key={id} label={label} checked={selected} onChange={(v) => onSelectionChange(id, v)} />
      ))}
    </FormGroup>
  </FormControl>
);

interface PriceChangesProps {
  mode: "MULTIPLIER" | "AMOUNT";
  changes: { id: PriceMode; label: string; value: number }[];
  disabled?: boolean;
  onChange: (id: PriceMode, value: number) => void;
}

const PriceChanges: FC<PriceChangesProps> = ({ mode, changes, disabled, onChange }) => (
  <FormControl sx={{ m: 3 }} component="fieldset" variant="standard" disabled={disabled}>
    <FormLabel component="legend">{mode === "MULTIPLIER" ? "Mnożniki" : "Kwoty"}</FormLabel>
    <FormGroup sx={{ gap: 1 }}>
      {changes.map(({ id, label, value }) => (
        <NumberField key={id} size="small" label={label} value={value} onChange={(v) => onChange(id, v ?? 0)} />
      ))}
    </FormGroup>
  </FormControl>
);

const ConvertOptionPrices = () => {
  const { options, productId } = useOutletContext<{
    productId: string;
    options: AdminProductOptionResponse[];
  }>();
  const { convertPrices } = useOptionPricesBatch(productId);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});
  const [sourcePriceList, setSourcePriceList] = useState<PriceMode>();
  const [targetPriceLists, setTargetPriceLists] = useState<PriceMode[]>([]);
  const [values, setValues] = useState<Record<PriceMode, number>>({} as Record<PriceMode, number>);
  const [valueMode, setValueMode] = useState<"MULTIPLIER" | "AMOUNT">("MULTIPLIER");
  const [everyOtherDay, setEveryOtherDay] = useState(false);
  const [ranges, setRanges] = useState([
    {
      dates: {
        start: dayjs().startOf("month").format(ISO_DATE_FORMAT),
        end: dayjs().endOf("month").format(ISO_DATE_FORMAT),
      },
      daysOfWeek: daysOfWeek.map(() => true),
    },
  ]);

  const handleSubmit = async () => {
    const options = Object.entries(selectedOptions)
      .filter(([, selected]) => selected)
      .map(([optionId]) => optionId);
    const source = sourcePriceList!;
    const targets = targetPriceLists.map((v) => ({ priceType: v, value: values[v] }));
    const requestBody = everyOtherDay
      ? {
          type: "EVERY_OTHER_DAY" as const,
          mode: valueMode,
          source,
          targets,
          options,
          dateRanges: ranges.map((r) => r.dates),
        }
      : {
          type: "DAYS_OF_WEEK" as const,
          mode: valueMode,
          source,
          targets,
          options,
          ranges: ranges.map(({ dates, daysOfWeek }) => ({
            dates,
            daysOfWeek: daysOfWeek.map((v, idx) => (v ? idx : null)).filter((v) => v !== null),
          })),
        };
    await convertPrices(requestBody);
  };

  const editEnabled = Object.values(selectedOptions).some((v) => v);
  const submitDisabled = !editEnabled || !sourcePriceList || targetPriceLists.length === 0;
  return (
    <Grid container spacing={2}>
      <Grid
        container
        size={3}
        spacing={1}
        sx={{
          alignContent: "center",
        }}
      >
        {options.map((o) => (
          <Option
            key={o.id}
            name={o.name}
            selected={selectedOptions[o.id] ?? false}
            onSelectionChange={(v) =>
              setSelectedOptions({
                ...selectedOptions,
                [o.id]: v,
              })
            }
          />
        ))}
      </Grid>
      <Grid
        container
        size={9}
        spacing={2}
        sx={{
          alignContent: "center",
        }}
      >
        <Grid size={3}>
          <PriceListSelect
            label="Cennik źródłowy"
            disabled={!editEnabled}
            values={priceLists.map((p) => ({ ...p, selected: sourcePriceList === p.id }))}
            onSelectionChange={(id, selected) => {
              setSourcePriceList(selected ? id : undefined);
              setTargetPriceLists([]);
            }}
          />
        </Grid>
        <Grid size={3}>
          <PriceListSelect
            label="Cenniki docelowe"
            disabled={!editEnabled}
            values={priceLists
              .filter((p) => p.id !== sourcePriceList)
              .map((p) => ({ ...p, selected: targetPriceLists.includes(p.id) }))}
            onSelectionChange={(id, selected) => {
              setTargetPriceLists((lists) => (selected ? [...lists, id] : lists.filter((l) => l !== id)));
              setValues((m) =>
                selected
                  ? { ...m, [id]: 0 }
                  : (Object.fromEntries(Object.entries(m).filter(([k]) => k !== id)) as Record<PriceMode, number>),
              );
            }}
          />
        </Grid>
        <Grid
          size={2}
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <ToggleButtonGroup
            color="primary"
            value={valueMode}
            orientation="vertical"
            exclusive
            onChange={(_, newValue) => setValueMode(newValue)}
          >
            <ToggleButton value="MULTIPLIER">Mnożnik</ToggleButton>
            <ToggleButton value="AMOUNT">Kwota</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid size={4}>
          <PriceChanges
            mode={valueMode}
            changes={possibleChanges(valueMode)
              .filter(({ id }) => targetPriceLists.includes(id))
              .map(({ id, label }) => ({ id, label, value: values[id] }))}
            disabled={!editEnabled}
            onChange={(id, v) => setValues((m) => ({ ...m, [id]: v }))}
          />
        </Grid>
        <Grid size={12}>
          <LabelledCheckbox
            label="Co drugi dzień"
            disabled={!editEnabled}
            checked={everyOtherDay}
            onChange={setEveryOtherDay}
          />
        </Grid>
        <Grid size={12}>
          <DateRangeGroup
            withDaysOfWeek={!everyOtherDay}
            ranges={ranges}
            disabled={!editEnabled}
            onChange={setRanges}
          />
        </Grid>
      </Grid>
      <Grid
        size={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <AsyncLoadingButton variant="outlined" onClick={handleSubmit} disabled={submitDisabled}>
          Aktualizuj cenniki
        </AsyncLoadingButton>
      </Grid>
    </Grid>
  );
};

export default ConvertOptionPrices;
